import { normalizeSelectQuestionType } from "@/lib/selectOtherOption";
import { canonicalizeUserTimezone } from "@/lib/userTimezone";

export type MomentusOptionsConfig = {
  enabled: boolean;
  days: 1 | 7 | 14 | 30;
  timezone: string;
};

export type MomentusOptionsSnapshot = {
  status: "loading" | "ready" | "error";
  openedAt: string;
  from: string;
  through: string;
  days: MomentusOptionsConfig["days"];
  timezone: string;
  choices: Array<{ value: string; text: string }>;
  allowOther: boolean;
};

export type MomentusResponseOptions = {
  openedAt: string;
  questions: Record<string, MomentusOptionsSnapshot>;
};

export function parseMomentusOptionsConfig(value: unknown): MomentusOptionsConfig | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const config = value as Record<string, unknown>;
  if (typeof config.enabled !== "boolean" || ![1, 7, 14, 30].includes(config.days as number)) {
    return null;
  }
  const timezone = canonicalizeUserTimezone(config.timezone);
  if (!timezone && (config.enabled || config.timezone !== "")) return null;
  return {
    enabled: config.enabled,
    days: config.days as MomentusOptionsConfig["days"],
    timezone: timezone || "",
  };
}

export function getMomentusOptionsConfig(question: {
  questionType?: unknown;
  options?: unknown;
}): MomentusOptionsConfig | null {
  if (!normalizeSelectQuestionType(question.questionType)) return null;
  const options = question.options;
  return parseMomentusOptionsConfig(
    options && typeof options === "object" && !Array.isArray(options)
      ? (options as Record<string, unknown>).momentusOptions
      : undefined,
  );
}

export function getMomentusOptionsDateRange(
  days: MomentusOptionsConfig["days"],
  timezone: string,
  openedAt = new Date(),
) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(openedAt);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)!.value);
  const today = Date.UTC(part("year"), part("month") - 1, part("day"));
  return {
    from: new Date(today - (days - 1) * 86_400_000).toISOString().slice(0, 10),
    through: new Date(today).toISOString().slice(0, 10),
  };
}
